// src/components/enterprise/RecoveryModelPanel.jsx
//
// THE ALBERTA RECOVERY MODEL TAB (cc-recovery), switched on per organization
// with features.enableRecoveryModel. Nothing here is a new figure: the panel
// regroups what the console already shows (the members' leg from the live
// snapshot or the edition's modeled example, the organization's own figures
// from Data Intake, the typed executive inputs) under the frame the
// Government of Alberta uses, so a ministry, a police service, a school
// division or a public program can see which objective each figure serves.
// Government wording comes from data/albertaRecoveryModel.js and nowhere
// else. Everything is aggregate at k >= 5; modeled figures are labelled; no
// partnership or data connection with any government body is claimed.

import React, { useMemo, useState } from 'react';
import {
    Landmark, Sprout, Activity, HeartHandshake, Compass, ArrowRight, ChevronDown, ChevronUp,
    Lock, Users, Building2, GraduationCap, Zap
} from 'lucide-react';
import { useEdition } from '../../data/editions/EditionContext';
import { useOrganization } from '../../context/OrganizationContext';
import { SourceTag, DisciplineFooter, MIN_GROUP_SIZE } from './shared/measurement';
import { METRIC_BY_ID, BUSINESS_INPUT_METRICS, formatMetricValue } from '../../data/workforceDictionary';
import { trendDelta } from '../../utils/executiveMetrics';
import { CONSOLE_TABS, consoleTabLabel } from './consoleTabs';
import {
    CONTINUUM, FOCUS_AREAS, OUTCOMES, PRINCIPLES, ATTRIBUTION, SOURCE_NOTE, PLAN_NOTE, PRIVACY_LINE,
    leadAreaFor, areaNoteFor
} from '../../data/albertaRecoveryModel';

const STAGE_ICONS = { prevention: Sprout, intervention: Activity, treatment: HeartHandshake, recovery: Compass };
const AREA_ICONS = { access: Users, barriers: Zap, facilities: Building2, immediate: Activity, youth: GraduationCap };
// The five executive inputs an admin can type, by dictionary id (the join used by the ROI tab).
const INPUT_BY_METRIC = Object.fromEntries(Object.entries(BUSINESS_INPUT_METRICS).map(([inputKey, metricId]) => [metricId, inputKey]));
const TAB_LABEL = Object.fromEntries(CONSOLE_TABS.map((t) => [t.id, consoleTabLabel(t)]));

const pct = (v) => (typeof v === 'number' ? `${Math.round(v * 100)}%` : null);
// A dotted snapshot path ('participation.rate') read off the live payload; null when absent.
const pick = (obj, path) => String(path || '').split('.').reduce((o, k) => (o && o[k] !== undefined ? o[k] : null), obj);

const STATUS = {
    live:    { label: 'Console live',     cls: 'text-emerald-700 bg-emerald-50 border-emerald-100' },
    onfile:  { label: 'Figures on file',  cls: 'text-[var(--brand-deep)] bg-blue-50 border-blue-100' },
    modeled: { label: 'Console modeled',  cls: 'text-amber-700 bg-amber-50 border-amber-100' },
    notyet:  { label: 'Nothing yet',      cls: 'text-slate-500 bg-slate-50 border-slate-200' }
};
const Chip = ({ children, cls }) => <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${cls}`}>{children}</span>;
const StatusChip = ({ status }) => { const s = STATUS[status] || STATUS.notyet; return <Chip cls={s.cls}>{s.label}</Chip>; };
const GovTag = () => <Chip cls="text-slate-600 bg-slate-50 border-slate-200">Government of Alberta</Chip>;
const RefChip = ({ children }) => <span className="text-[10px] font-black tabular-nums px-1.5 py-0.5 rounded-md bg-[var(--brand-deep)] text-white shrink-0">{children}</span>;

const SectionHead = ({ kicker, title, sub, right }) => (
    <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
            <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">{kicker}</p>
            <h2 className="text-lg font-black text-[var(--brand-deep)]">{title}</h2>
            {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
        </div>
        {right && <div className="flex items-center gap-2">{right}</div>}
    </div>
);

// One figure under a continuum stage: a value when the console has it, else a short note on when it arrives.
const Row = ({ label, value, note, sub }) => (
    <div className="flex items-start justify-between gap-3 py-1.5 border-b border-slate-100 last:border-0">
        <span className="text-xs text-slate-500 leading-snug">{label}</span>
        <span className="text-right max-w-[60%]">
            {value !== null && value !== undefined
                ? <span className="text-sm font-bold text-slate-800 tabular-nums leading-tight">{value}</span>
                : <span className="text-[11px] text-slate-400">{note || 'pending'}</span>}
            {sub && value !== null && value !== undefined && <span className="block text-[10px] text-slate-400">{sub}</span>}
        </span>
    </div>
);

const RecoveryModelPanel = ({ live, metrics, onNavigate }) => {
    const edition = useEdition();
    const { orgData } = useOrganization();
    const orgName = orgData?.name || 'this organization';
    const go = (tab) => () => { if (onNavigate) onNavigate(tab); };
    const isLive = live?.source === 'live';
    const locale = edition.roi?.locale || 'en-CA';
    const currency = edition.roi?.currency || 'CAD';
    const lead = leadAreaFor(edition);
    const popRisk = edition.populationRisk || null;
    const cohort = metrics?.cohort || null;
    const [open, setOpen] = useState({});

    // The organization's own figure for a dictionary id: an org-wide upload,
    // else the groups that carry it, else an executive input an admin typed.
    // Modeled and benchmark inputs are never shown as the organization's own.
    const figures = useMemo(() => {
        const inputs = metrics?.inputs || {};
        const resolve = (id) => {
            const m = METRIC_BY_ID[id] || null;
            const label = m ? m.label : id.replace(/_/g, ' ');
            const orgWide = live?.business?.[id];
            if (orgWide && typeof orgWide.value === 'number') return { id, label, m, value: orgWide.value, scope: 'org', source: orgWide.source || 'upload', asOf: orgWide.asOf || null };
            const groups = Object.values(live?.businessByGroup || {}).filter((g) => g && g[id] && typeof g[id].value === 'number').length;
            if (groups) return { id, label, m, value: null, scope: 'groups', groups, source: 'upload' };
            const k = INPUT_BY_METRIC[id];
            const row = k ? inputs[k] : null;
            if (row && (row.source === 'client' || row.source === 'upload') && typeof row.value === 'number') return { id, label, m, value: row.value, scope: 'org', source: row.source === 'client' ? 'typed' : 'upload' };
            return null;
        };
        return { resolve, onFile: (ids) => (ids || []).map(resolve).filter(Boolean) };
    }, [live, metrics]);

    // The members' leg per stage: live snapshot when real, else the edition's
    // modeled example (labelled), and nothing invented where the example has no figure.
    const stage = useMemo(() => {
        const q = (isLive && live.quadrants) || {};
        const responded = isLive ? (live.participation?.responded ?? null) : (cohort?.responded ?? null);
        const rate = isLive ? (live.participation?.rate ?? null) : (edition.summary?.participationRate ?? null);
        const well = (typeof q.thriving === 'number' || typeof q.building === 'number') ? (q.thriving || 0) + (q.building || 0) : null;
        const runningHot = isLive ? (q.running_hot ?? null) : (cohort?.runningHotShare ?? null);
        const needsSupport = isLive ? (q.needs_support ?? null) : null;
        const groups = isLive
            ? (live.groups || []).filter((g) => !g.suppressed && (typeof g.runningHotShare === 'number' || typeof g.pressure === 'number'))
                .sort((a, b) => ((b.runningHotShare ?? -1) - (a.runningHotShare ?? -1)) || ((b.pressure ?? -1) - (a.pressure ?? -1))).slice(0, 2)
            : (edition.readiness?.groups || []).filter((g) => g.responses >= MIN_GROUP_SIZE).sort((a, b) => (b.pressure || 0) - (a.pressure || 0)).slice(0, 2);
        // undefined: the edition has no population screen, so the row is not drawn.
        const gambling = popRisk && popRisk.metric === 'gambling' ? (isLive ? (live.gambling?.riskShare ?? null) : null) : undefined;
        const trend = (isLive && live.capacityTrend?.length) ? live.capacityTrend : (edition.capacityTrend || []);
        const points = trend.filter((p) => p && typeof p.capacity === 'number');
        const delta = trendDelta(trend);
        const movement = points.length > 1 ? `${delta >= 0 ? '+' : ''}${delta} since ${isLive ? points[0].date : 'the first modeled reading'}` : null;
        return { responded, rate, well, runningHot, needsSupport, groups, gambling, movement };
    }, [isLive, live, cohort, edition, popRisk]);

    const rowsFor = (read) => {
        switch (read.id) {
            case 'responded': return [<Row key="responded" label={read.label} value={typeof stage.responded === 'number' ? new Intl.NumberFormat(edition.roi?.locale || 'en-CA').format(stage.responded) : null} note="with the first check-ins" />];
            case 'participation': return [<Row key="participation" label={read.label} value={typeof stage.rate === 'number' ? `${stage.rate}%` : null} note="needs the member count" />];
            case 'well_share': return [<Row key="well" label={read.label} value={pct(stage.well)} note={isLive ? 'with the next scored check-ins' : 'with live check-ins'} />];
            case 'running_hot': return [<Row key="rh" label={read.label} value={pct(stage.runningHot)} note="with the next scored check-ins" />];
            case 'needs_support': return [<Row key="ns" label={read.label} value={pct(stage.needsSupport)} note={isLive ? 'with the next scored check-ins' : 'with live check-ins'} />];
            case 'strain_groups': return [<Row key="groups" label={read.label} value={stage.groups.length ? stage.groups.map((g) => g.name).join(', ') : null} note={`no group at ${MIN_GROUP_SIZE}+ yet`} />];
            case 'population_risk': {
                if (stage.gambling === undefined) return [];
                return [<Row key="risk" label={popRisk.detail ? `${read.label} (${popRisk.detail})` : read.label} value={pct(stage.gambling)} note={isLive ? `under ${MIN_GROUP_SIZE} screened` : 'with live check-ins'} />];
            }
            case 'baseline_movement': return [<Row key="move" label={read.label} value={stage.movement} note="needs two readings" />];
            case 'org_figures': {
                const list = figures.onFile(read.metrics);
                if (!list.length) {
                    return [
                        <div key="none" className="py-1.5 flex items-center justify-between gap-2 text-[11px] text-slate-400">
                            <span>No figure on file yet</span>
                            <button type="button" onClick={go('cc-data')} className="font-bold text-[var(--brand-blue)] hover:underline inline-flex items-center gap-1">Data Intake <ArrowRight size={11} /></button>
                        </div>
                    ];
                }
                return list.map((f) => (
                    <Row
                        key={f.id}
                        label={f.label}
                        value={f.scope === 'groups' ? `${f.groups} group${f.groups === 1 ? '' : 's'}` : formatMetricValue(f.m, f.value, locale, currency)}
                        sub={f.scope === 'groups' ? 'by group' : (f.source === 'typed' ? 'typed' : 'uploaded')}
                    />
                ));
            }
            case 'fixed_line': return [<p key="fixed" className="pt-2 text-[11px] text-slate-500 leading-snug flex items-start gap-1.5"><Lock size={11} className="mt-0.5 shrink-0" /> {read.label}</p>];
            default: return [];
        }
    };

    // Status of a focus area for THIS organization: a live snapshot key behind
    // it, else one of its figures on file, else the modeled example, else not yet.
    const areaStatus = (area) => {
        const sig = area.signals || {};
        const paths = [...(sig.snapshot || []), ...(lead === area.id ? (sig.leadSnapshot || []) : [])];
        const hasLive = isLive && paths.some((p) => { const v = pick(live, p); return Array.isArray(v) ? v.some((g) => g && !g.suppressed) : typeof v === 'number'; });
        if (hasLive) return 'live';
        if (figures.onFile(sig.metrics).length) return 'onfile';
        if (!isLive && paths.length) return 'modeled';
        return 'notyet';
    };

    // Government order, the edition's lead area first.
    const areas = useMemo(() => (lead ? [...FOCUS_AREAS.filter((a) => a.id === lead), ...FOCUS_AREAS.filter((a) => a.id !== lead)] : FOCUS_AREAS), [lead]);

    // Business-plan objectives in plan order, or the edition's preferred refs first.
    const preferred = edition.recoveryModel?.objectives || [];
    const rank = (ref) => { const i = preferred.indexOf(ref); return i === -1 ? preferred.length : i; };
    const orderObjectives = (list) => (preferred.length ? [...list].sort((a, b) => rank(a.ref) - rank(b.ref)) : list);
    const toggle = (id) => setOpen((o) => ({ ...o, [id]: !o[id] }));

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
            {/* THE FRAME */}
            <div className="bg-[var(--brand-deep)] rounded-3xl shadow-md p-6 md:p-8 flex flex-col lg:flex-row gap-6 lg:items-center">
                <div className="flex-1 space-y-3">
                    <div className="flex items-center gap-3 flex-wrap">
                        <Landmark className="text-[var(--brand-yellow)]" size={24} />
                        <h2 className="text-xl font-semibold text-white tracking-wide">The Alberta Recovery Model</h2>
                        <SourceTag dark modeled={!isLive} />
                    </div>
                    <p className="text-slate-300 text-sm leading-relaxed font-light max-w-2xl">
                        The figures this console already shows, regrouped under the frame the Government of Alberta uses: a continuum of prevention, intervention, treatment and recovery, five key focus areas, the ministry&apos;s 2025-28 outcomes and five key principles. Nothing here is a new number. Every figure is aggregate, at groups of {MIN_GROUP_SIZE} or more.
                    </p>
                </div>
                <div className="w-full lg:w-[300px] bg-white/5 p-5 rounded-2xl border border-white/10 shrink-0">
                    <p className="text-[var(--brand-yellow)] font-semibold text-[11px] uppercase tracking-widest mb-2">What EAS is here</p>
                    <p className="text-slate-200 text-sm leading-relaxed font-light">EAS delivers no treatment. It sits in front of the services the province funds, points people to them, and counts the contact in aggregate.</p>
                </div>
            </div>

            {/* 1. THE CONTINUUM */}
            <section>
                <SectionHead kicker="The continuum" title="Prevention, intervention, treatment, recovery" sub={`What the console reads at each stage for ${orgName}.`} right={<SourceTag modeled={!isLive} />} />
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                    {CONTINUUM.map((s, i) => {
                        const Icon = STAGE_ICONS[s.id] || Activity;
                        return (
                            <div key={s.id} className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col">
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="text-[10px] font-black text-slate-400 tabular-nums">{i + 1}</span>
                                    <div className="w-8 h-8 rounded-lg bg-blue-50 text-[var(--brand-deep)] flex items-center justify-center shrink-0"><Icon size={16} /></div>
                                    <h3 className="font-black text-[var(--brand-deep)]">{s.label}</h3>
                                </div>
                                <p className="text-xs text-slate-500 leading-snug mb-3">{s.eas}</p>
                                <div className="flex-1">{s.reads.flatMap((r) => rowsFor(r))}</div>
                                <button type="button" onClick={go(s.tab)} className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-1 text-[11px] font-semibold text-[var(--brand-blue)] hover:gap-2 transition-all">
                                    Open {TAB_LABEL[s.tab] || 'the detail'} <ArrowRight size={13} />
                                </button>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* 2. FIVE KEY FOCUS AREAS */}
            <section>
                <SectionHead kicker="Five key focus areas" title="Where each focus area meets this console" sub={lead ? `In the government's order, with the area ${orgName} starts from first.` : "In the government's order."} right={<GovTag />} />
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {areas.map((a) => {
                        const Icon = AREA_ICONS[a.id] || Landmark;
                        const isLead = lead === a.id;
                        return (
                            <div key={a.id} className={`bg-white rounded-2xl p-5 flex flex-col border ${isLead ? 'border-[var(--brand-blue)] shadow-md' : 'border-slate-200'}`}>
                                {isLead && <p className="text-[10px] font-black uppercase tracking-wider text-[var(--brand-blue)] mb-2">Where {orgName} starts</p>}
                                <div className="flex items-start justify-between gap-3 mb-3">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <div className="w-9 h-9 rounded-lg bg-blue-50 text-[var(--brand-deep)] flex items-center justify-center shrink-0"><Icon size={17} /></div>
                                        <h3 className="font-black text-[var(--brand-deep)] leading-tight">{a.title}</h3>
                                    </div>
                                </div>
                                <div className="mb-2"><GovTag /></div>
                                <ul className="space-y-1.5 mb-4">
                                    {a.government.map((g, i) => <li key={i} className="text-xs text-slate-600 leading-snug">{g}</li>)}
                                </ul>
                                <div className="mt-auto pt-3 border-t border-slate-100">
                                    <div className="flex items-center justify-between gap-2 mb-1"><p className="text-[10px] font-black uppercase tracking-wider text-slate-400">What EAS adds here</p><StatusChip status={areaStatus(a)} /></div>
                                    <p className="text-xs text-slate-700 leading-snug">{areaNoteFor(edition, a.id) || a.eas}</p>
                                    <button type="button" onClick={go(a.tab)} className="mt-3 flex items-center gap-1 text-[11px] font-semibold text-[var(--brand-blue)] hover:gap-2 transition-all">
                                        Open {TAB_LABEL[a.tab] || 'the detail'} <ArrowRight size={13} />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* 3. THE MINISTRY'S 2025-28 OUTCOMES */}
            <section>
                <SectionHead kicker={PLAN_NOTE} title="The four outcomes, and what this console informs" sub="Open a card for the objectives the console informs and the measures it relates to. EAS informs a measure; it never claims to move one." right={<GovTag />} />
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {OUTCOMES.map((o) => {
                        const isOpen = !!open[o.id];
                        const objectives = orderObjectives(o.objectives);
                        return (
                            <div key={o.id} className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                                <button type="button" onClick={() => toggle(o.id)} aria-expanded={isOpen} className="w-full text-left p-5 hover:bg-slate-50/60 transition-colors">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">{o.title}</p>
                                            <p className="text-sm font-semibold text-slate-800 leading-snug mt-1">{o.statement}</p>
                                        </div>
                                        {isOpen ? <ChevronUp size={18} className="text-slate-400 shrink-0 mt-1" /> : <ChevronDown size={18} className="text-slate-400 shrink-0 mt-1" />}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-1.5 mt-3">
                                        <span className="text-[10px] text-slate-400 mr-1">Objectives</span>
                                        {objectives.map((ob) => <RefChip key={ob.ref}>{ob.ref}</RefChip>)}
                                        <span className="text-[10px] text-slate-400 ml-2 mr-1">Measured by</span>
                                        {o.measures.map((m) => <RefChip key={m.ref}>{m.ref}</RefChip>)}
                                    </div>
                                </button>
                                {isOpen && (
                                    <div className="px-5 pb-5 space-y-4 animate-in fade-in">
                                        <div>
                                            <p className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2">Objectives the console informs</p>
                                            <ul className="space-y-3">
                                                {objectives.map((ob) => (
                                                    <li key={ob.ref} className="flex items-start gap-3">
                                                        <RefChip>{ob.ref}</RefChip>
                                                        <div className="min-w-0">
                                                            <p className="text-xs text-slate-700 leading-snug">{ob.text}</p>
                                                            <p className="text-[11px] text-[var(--brand-deep)] leading-snug mt-1"><span className="font-bold">How: </span>{ob.eas}</p>
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                        <div>
                                            <p className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2">Measures and indicators</p>
                                            <ul className="space-y-3">
                                                {o.measures.map((m) => (
                                                    <li key={m.ref} className="flex items-start gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                                                        <RefChip>{m.ref}</RefChip>
                                                        <div className="min-w-0">
                                                            <p className="text-xs font-bold text-slate-800">{m.name} <span className="font-normal text-slate-400">({m.kind})</span></p>
                                                            <p className="text-[11px] text-slate-600 leading-snug">{m.description}</p>
                                                            <p className="text-[11px] text-slate-700 mt-1">
                                                                <span className="font-bold">Baseline</span> {m.baseline}
                                                                {m.targets && <><span className="font-bold ml-2">Targets</span> {m.targets}</>}
                                                            </p>
                                                            <p className="text-[11px] text-[var(--brand-deep)] leading-snug mt-1"><span className="font-bold">EAS: </span>{m.eas}</p>
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* 4. FIVE KEY PRINCIPLES */}
            <section>
                <SectionHead kicker="Five key principles" title="How the console honours them" />
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                    {PRINCIPLES.map((p) => (
                        <div key={p.id} className="bg-white border border-slate-200 rounded-2xl p-4">
                            <p className="font-black text-[var(--brand-deep)] text-sm">{p.label}</p>
                            <p className="text-xs text-slate-500 leading-snug mt-1">{p.eas}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* 5. FOOTER */}
            <DisciplineFooter
                showLegend={false}
                caption={(
                    <>
                        <span className="block">{ATTRIBUTION}</span>
                        <span className="block mt-1.5">Sources: {SOURCE_NOTE}; Ministry of Mental Health and Addiction Business Plan 2025-28.</span>
                        <span className="block mt-1.5">{PRIVACY_LINE}</span>
                        <span className="block mt-1.5">
                            {isLive
                                ? `Member figures are live: check-ins from the last ${live.windowDays || 90} days, each member counted once at their latest answers, in groups of ${MIN_GROUP_SIZE} or more. The organization's own figures carry their source and date.`
                                : `Member figures are the edition's modeled example until ${MIN_GROUP_SIZE} members in a group have checked in, and are labelled Modeled. The organization's own figures, where shown, are its own and carry their source.`}
                        </span>
                    </>
                )}
            />
        </div>
    );
};

export default RecoveryModelPanel;
